/**
 * Detail "Set blackout…" — the outline bulk handler, run against a selection of one.
 *
 * The board's record ⌘K and its bulk ⌘K must offer one row the same actions, so this synthesises the
 * outline row the board would have handed over and calls `handleOutlineSetBlackout` with it.
 */
import { computed, inject } from '@angular/core';
import { Entity } from '@jms/model';
import { EntityDataService, EntityDataServiceRaw } from '@workbuddy/services/entities';
import { ToastService } from '@workbuddy/ui/services';
import { WorkBuddyDialogService } from '@workbuddy/ui/v2/blocks/dialog';
import { BOARD_PANEL_HOST } from '@workbuddy/ui/v2/blocks/entity.board/components/board.panel/board.panel.host';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import type { ActionCommandCapability, ActionCommandContext, ActionCommandDescriptor, SurfaceContextLike } from '@workbuddy/ui/v2/surface.engine';
import { handleOutlineSetBlackout, OUTLINE_BLACKOUT_ICON, OUTLINE_BLACKOUT_LABEL, OUTLINE_SET_BLACKOUT_ACTION } from '../bulk/outline.bulk.blackout';
import { BlackoutOwnerRanges } from '../dialogs/outline.set.blackout';
import { reloadSurfaceRecord } from '../bulk/outline.record.command';
import { outlineRowForLine, singleOutlineCommandArgs } from '../bulk/outline.record';
import { OutlineLineCommandConfig } from './outline.detail.command.config';

export function createOutlineSetBlackoutCommand<TLine extends Entity & BlackoutOwnerRanges>(config: OutlineLineCommandConfig): ActionCommandCapability<TLine> {

  const dialogs = inject(WorkBuddyDialogService);
  const entityData = inject(EntityDataService);
  const entityDataRaw = inject(EntityDataServiceRaw);
  const toasts = inject(ToastService);
  const board = inject(EntityBoardStore, { optional: true });
  const panel = inject(BOARD_PANEL_HOST, { optional: true });

  return {
    descriptor: ({ page }: ActionCommandContext<TLine>) => computed<ActionCommandDescriptor>(() => ({
      id: OUTLINE_SET_BLACKOUT_ACTION,
      label: OUTLINE_BLACKOUT_LABEL,
      iconLeft: OUTLINE_BLACKOUT_ICON,
      hidden: !page.$model()?._id || page.$readonly(),
      disabled: page.$saving(),
      pin: 'never',
    })),
    run: async (page: SurfaceContextLike<TLine>) => {

      const record = page.$model();
      const row = record && !page.$readonly() ? outlineRowForLine(record, config.kinds) : null;
      if (!row || !record) return;

      // The Detail already holds the line, so `loadLine` hands the handler the ranges it is about to
      // merge into rather than making it re-read the record the surface is displaying.
      const outcome = await handleOutlineSetBlackout(singleOutlineCommandArgs(OUTLINE_SET_BLACKOUT_ACTION, row), { kinds: config.kinds, dialogs, entityData, entityDataRaw, toasts, loadLine: async () => record });
      // `clearSelection: false` is how the handler says the dialog was dismissed or nothing applied.
      if (outcome.clearSelection === false) return;
      await reloadSurfaceRecord(entityDataRaw, config.entity, page, record._id);
      if (!board) panel?.refresh('collection');
    },
  };
}
